/**
 * The signed-in user's saved words.
 *
 * No loader, and deliberately so: the Firebase ID token lives in JS memory
 * rather than a cookie, so an SSR document request for this page is anonymous
 * and there is nothing to fetch server-side. The route renders the chrome and a
 * placeholder; the store fills it in once /api/me has answered on the client.
 *
 * public/robots.txt already carries `Disallow: /myaccount/`, and
 * src/server/sitemap.ts builds from a fixed page list rather than scanning
 * routes — so nothing there needs changing for this page, and nothing should be
 * added.
 */

import { createFileRoute, useSearch } from '@tanstack/react-router'
import SavedWordsPage from 'pages/myaccount/saved'

type SavedSearch = {
   /** A tag id, or the 'none' sentinel. Never a tag *name* — a rename must not
    *  break a bookmarked list. */
   tag?: string
   q?: string
}

function SavedWords() {
   // useSearch({ from }) rather than Route.useSearch(): the component is
   // referenced by the route it would otherwise read from, and that cycle is
   // exactly what no-use-before-define objects to.
   const { tag, q } = useSearch({ from: '/myaccount/saved' })
   return <SavedWordsPage tag={tag} query={q ?? ''} />
}

export const Route = createFileRoute('/myaccount/saved')({
   // Search state lives in the URL so back and forward work and a filtered
   // list can be bookmarked. `sort` is deliberately not among them any more:
   // nothing on the page offers an order to choose, so accepting one would
   // leave a URL parameter with no way to reach it and no way to unset it.
   validateSearch: (search: Record<string, unknown>): SavedSearch => ({
      tag: typeof search.tag === 'string' ? search.tag : undefined,
      q: typeof search.q === 'string' && search.q.length > 0 ? search.q : undefined,
   }),
   head: () => ({
      meta: [
         { title: 'Saved words | thai2english' },
         { name: 'robots', content: 'noindex, nofollow' },
      ],
   }),
   // The shell is user-agnostic, but marking it private removes a whole
   // category of future mistake, and an SSR placeholder is worthless to cache.
   headers: () => ({ 'Cache-Control': 'private, no-store' }),
   component: SavedWords,
})
